import { Component } from '@angular/core';
import { Observable } from 'rxjs';

import { AuthUser } from '@app/account/account-api.model';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { AuthService } from '@app/shared/services/auth.service';
import { ResourceImageService } from '@app/shared/services/resource-image/resource-image.service';
import { ThumbnailService } from '@app/thumbnails/thumbnail.service';
import {
  PathwayAuthoring,
  PathwayFacade,
  PathwayViewModel,
  PathwayModalSettingsChecks,
  getHeroImageSrc,
} from '@dg/pathways-rsm';
import { TranslateService } from '@ngx-translate/core';
import { LDFlagsService } from '@dg/shared-services';

@Component({
  selector: 'dgx-pathway-header',
  templateUrl: './pathway-header.component.html',
})
export class PathwayHeaderComponent extends SubscriberBaseDirective {
  public isOrgUser: boolean = false;
  public showPageActions: boolean = false;
  public imageUrl: string;
  public i18n = this.translate.instant([
    'Pathways_Collaborators',
    'Pathways_Following',
    'Pathways_OptionalItemCountSingular',
    'Core_Completed',
    'Core_EndorsedTooltip',
  ]);
  public vm$: Observable<PathwayViewModel>;

  private authUser: AuthUser;

  constructor(
    private authService: AuthService,
    private facade: PathwayFacade,
    private resourceImageService: ResourceImageService,
    private thumbnailService: ThumbnailService,
    private translate: TranslateService,
    private authoring: PathwayAuthoring,
    private ldFlagsService: LDFlagsService
  ) {
    super();
    this.vm$ = this.facade.vm$;
  }

  /** Display authors conditionally */
  public get displayPathwayAuthors(): boolean {
    return (
      (this.facade.snapshot.permissions.canViewAuthors &&
        !!this.facade.snapshot.pathway?.authors?.length) ||
      (this.facade.snapshot.isEditMode && this.isOrgUser)
    );
  }

  public ngOnInit(): void {
    this.authUser = this.authService.authUser;
    this.showPageActions = !!this.authUser;
    this.isOrgUser = !!this.authUser?.orgInfo?.length;
  }

  /**
   * Create the display image source.
   *
   * @param newImageUrl image to be updated with the source
   * @returns string of image src
   */
  public displayImageSrcset(newImageUrl: string): string {
    return getHeroImageSrc(
      this.resourceImageService,
      this.thumbnailService,
      newImageUrl
    );
  }

  public async editAuthors(): Promise<void> {
    const checks: PathwayModalSettingsChecks = {
      isChannel: this.facade.snapshot.isChannel,
      disableLearnerSkillsRegistry:
        this.facade.snapshot.disableLearnerSkillsRegistry,
      showPathwayBadgeTrigger: this.facade.snapshot.showPathwayBadgeTrigger,
      isConsumerUser: this.facade.snapshot.isConsumerUser,
    };
    await this.authoring.editAuthors(checks);
  }

  public async setVisibilityLevel(): Promise<void> {
    if (this.ldFlagsService.pathwayNotificationOwnership) {
      const checks: PathwayModalSettingsChecks = {
        isChannel: this.facade.snapshot.isChannel,
        disableLearnerSkillsRegistry:
          this.facade.snapshot.disableLearnerSkillsRegistry,
        showPathwayBadgeTrigger: this.facade.snapshot.showPathwayBadgeTrigger,
        isConsumerUser: this.facade.snapshot.isConsumerUser,
      };
      await this.authoring.showSettings(checks);
    } else {
      await this.authoring.updatePathwayVisibility(
        this.facade.snapshot.pathway.privacyLevel
      );
    }
  }
}
